import React, { useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { MoneyApi } from "go-core/api/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { apiPublic } from "../../../../../../../../../services/Api/Public/apiPublic";
import { OrderPaymentMethodApi, OrderTransactionApi } from "../../../../../../../../../services/Api/Public/types";
import { OrderContext } from "../../../../../contexts/OrderContext";
import { ApplePayPaymentRequest } from "../../../../../services/types";
import OrderPaymentInfo from "../../OrderPaymentInfo";

interface Props {
	totalPrice: MoneyApi;
	validatePaymentDataOnClick: () => boolean;
	paymentMethod: OrderPaymentMethodApi;
	handlePay: (
		paymentData: string,
		callback?: (data: OrderTransactionApi) => Promise<void> | void
	) => (e?: React.BaseSyntheticEvent) => Promise<void>;
	checkTransaction: (data: OrderTransactionApi) => Promise<void>;
}

const PayViaApplePayButton = ({
	totalPrice,
	validatePaymentDataOnClick,
	paymentMethod,
	handlePay,
	checkTransaction,
}: Props) => {
	const { addFlash } = useFlash();
	const { t } = useTranslation();
	const { orderId } = useContext(OrderContext);
	const submitButtonRef = useRef<HTMLButtonElement | null>(null);
	const applePayButtonRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		const el = applePayButtonRef.current;
		if (!el) return;

		el.addEventListener("click", onApplePayButtonClicked);

		return () => {
			el.removeEventListener("click", onApplePayButtonClicked);
		};
	}, [applePayButtonRef]);

	const paymentCallback = (session: ApplePaySession, data: OrderTransactionApi) => {
		session.completePayment(ApplePaySession.STATUS_SUCCESS);
		checkTransaction(data);
	};

	const getDisplayNameDependingOnDomain = (domain: string) => {
		switch (domain) {
			case "demo2.gopos.pl":
				return "GoPOS Demo";
			case "demoshop.gopos.pl":
				return "GoPOS Shop Demo";
			default:
				return "";
		}
	};

	const onApplePayButtonClicked = () => {
		submitButtonRef?.current?.click();
	};

	const submitForm = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (!window.ApplePaySession) {
			return;
		}

		const isValid = validatePaymentDataOnClick();

		if (!isValid) {
			event.preventDefault();
			return;
		}

		try {
			const request: ApplePayPaymentRequest = {
				countryCode: "PL",
				currencyCode: totalPrice.currency,
				merchantCapabilities: ["supports3DS"],
				supportedNetworks: ["visa", "masterCard"],
				total: { label: t("modules.e_order.field.amount_to_pay.title"), amount: totalPrice.amount.toString() },
			};

			const session = new ApplePaySession(3, request);

			const domain = window.self !== window.top ? new URL(document.referrer).hostname : window.location.hostname;

			session.onvalidatemerchant = async (event) => {
				if (!orderId) return;
				try {
					const res = await apiPublic.getApplePaySession(orderId, {
						validationUrl: event.validationURL,
						paymentMethodId: paymentMethod.id,
						domainName: domain,
						displayName: getDisplayNameDependingOnDomain(domain),
					});
					session.completeMerchantValidation(JSON.parse(atob(res.session)));
				} catch (err) {
					handleError.alert(err, addFlash);
					session.abort();
				}
			};

			session.onpaymentauthorized = async (event) => {
				try {
					const paymentToken = btoa(JSON.stringify(event.payment.token.paymentData));

					await handlePay(paymentToken, (data) => paymentCallback(session, data))();
				} catch (err) {
					session.completePayment(ApplePaySession.STATUS_FAILURE);
					session.abort();
				}
			};

			session.oncancel = () => {};

			session.begin();
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	return (
		<div
			className="d-flex flex-column button-payment-container apple-pay-button-container"
			data-testid="e-order-apple-pay-button"
		>
			<div className="d-flex align-items-center justify-content-between">
				<p className="mb-0 text-muted">{`${t("modules.e_order.field.total_price.title")}:`}</p>
				<p className="mb-0">{FormatMoney(totalPrice)}</p>
			</div>
			<button className="d-none" ref={submitButtonRef} onClick={submitForm} type="button" />
			<div className="w-100">
				<apple-pay-button ref={applePayButtonRef} buttonstyle="black" type="buy" locale={navigator.language} />
			</div>
			<OrderPaymentInfo centerText />
		</div>
	);
};

export default PayViaApplePayButton;
